import type { Currency } from '../enums/currency';
import PowerduckState from '../../app/powerduck-state';
import LanguageTranslator from '../enum-translation/language-translator';
import { Language } from '../enums/language';
import { sortBy } from '../extensions/array-extensions';

export class LanguageUtils {
	static supportedLanguages: Language[] = [
		Language.sk,
		Language.en,
	];

	private static cachedLanguageList: LanguageUtils.LanguageListItem[] | null = null;

	private static readonly currencyMap: Record<string, string> = {
		PLN: 'zł',
		CZK: 'kč',
		EUR: '€',
		HUF: 'Ft',
	};

	private static readonly currencyRegex = new RegExp(Object.keys(LanguageUtils.currencyMap).join('|'), 'g');

	private static getAdjectiveName(name: string): string {
		if (name.endsWith('ý')) {
			name = `${name.substring(0, name.length - 1)}y`;
		}

		return name;
	}

	private static normalizeCurrency(value: string): string {
		return value.replace(LanguageUtils.currencyRegex, match => LanguageUtils.currencyMap[match]);
	}

	static getLanguageList(sort?: boolean): Array<LanguageUtils.LanguageListItem> {
		if (LanguageUtils.cachedLanguageList) {
			return LanguageUtils.cachedLanguageList;
		}

		const retVal: Array<LanguageUtils.LanguageListItem> = [];

		const getLanguage = (langCode: Language): LanguageUtils.LanguageListItem => {
			const newOption = <LanguageUtils.LanguageListItem>{
				id: langCode,
				text: LanguageUtils.getAdjectiveName(LanguageTranslator.getString(langCode)),
				value: langCode.toString(),
				flag: null,
			};

			switch (langCode) {
				case 'sk':
					newOption.flag = 'svk';
					break;
				case 'en':
					newOption.flag = 'gbr';
					break;
				case 'cs':
					newOption.flag = 'cze';
					break;
				case 'de':
					newOption.flag = 'ger';
					break;
				case 'pl':
					newOption.flag = 'pol';
					break;
				case 'it':
					newOption.flag = 'ita';
					break;
				case 'hu':
					newOption.flag = 'hun';
					break;
				default:
					break;
			}

			return newOption;
		};

		for (const langCode of [
			Language.sk,
			Language.en,
			Language.cs,
			Language,
			Language.pl,
			Language.it,
			Language.hu,
			Language.de,
		]) {
			if (LanguageUtils.supportedLanguages.includes(langCode as any)) {
				retVal.push(getLanguage(langCode as any));
			}
		}

		if (sort === true) {
			try {
				retVal.sort((a, b) => a.text.localeCompare(b.text));
			} catch (e) {
				retVal[sortBy](p => p.text);
			}
		}

		LanguageUtils.cachedLanguageList = retVal;
		return retVal;
	}

	/**
	 * Returns enum value from string language code
	 *
	 * @param langCode 2-letter language code [sk, en, cs...]
	 */
	static getLanguageEnum(langCode: string): Language {
		return (langCode as any) ?? Language.en;
	}

	static getLanguageFlagUrl(languageFlag: string, svg?: boolean): string {
		if (!svg) {
			return `${PowerduckState.getAssetRootDirectory()}/img/flags/${languageFlag}.png`;
		} else {
			return `${PowerduckState.getAssetRootDirectory()}/img/flags/svg/${languageFlag}.svg`;
		}
	}

	static floatToCurrency(
		value: number,
		currency: string | Currency | number | any,
		killZeroCents?: boolean,
	): string {
		if (value == null) {
			value = 0;
		}

		let currencyISO: string;
		if (currency == null || currency == '') {
			currencyISO = '?';
		} else {
			currencyISO = currency as any;
		}

		if (killZeroCents == null) {
			killZeroCents = true;
		}

		if (killZeroCents) {
			try {
				return LanguageUtils.normalizeCurrency((Math.round(value * 100) / 100).toLocaleString(PowerduckState.getCurrentLanguage(), {
					style: 'currency',
					currency: currencyISO,
				})).replace(' ,00', '').replace(' .00', '').replace(',00', '').replace('.00', '');
			} catch (e) {
				return (`${(Math.round(value * 100) / 100).toString()} ${currencyISO}`).replace(' ,00', '').replace(' .00', '').replace(',00', '').replace('.00', '');
			}
		} else {
			try {
				return LanguageUtils.normalizeCurrency((Math.round(value * 100) / 100).toLocaleString(PowerduckState.getCurrentLanguage(), {
					style: 'currency',
					currency: currencyISO,
				}));
			} catch (e) {
				return `${(Math.round(value * 100) / 100).toString()} ${currencyISO}`;
			}
		}
	}

	static negateString(str: string): string {
		return PowerduckState.getResourceValue('negationBase') + str.toLowerCase();
	}
}

// Type-only namespace merge so `LanguageUtils.LanguageListItem` keeps working
// for callers that use it as a type annotation. Only declares an interface —
// no runtime members, no `let`/`var`/`function` exports, so Oxc accepts it.
// eslint-disable-next-line ts/no-namespace
export namespace LanguageUtils {
	export interface LanguageListItem {
		text: string;
		value: string;
		flag: string;
		id: Language;
	}
}
